import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Pagination from '../components/Pagination';
import { useCapability } from '../utils/capabilityContext';
import { listDebugPatients, seedFollowUp, resetPatient } from '../api/adminDebugApi';
import { getAdminToken } from '../utils/adminAuth';
import { formatDateTime, formatPatientName, normalizeUiLocale } from '../utils/locale';

const emptySeedForm = {
  mrn: '',
  phone: '',
  clinic_id: '',
  doctor_id: '',
  patient_name: ''
};

const emptyResetForm = {
  mrn: '',
  phone: '',
  delete_patient: false
};

const emptyPatientFilters = {
  mrn: '',
  phone: '',
  first_name: '',
  last_name: ''
};

const DEMO_SEED_SCENARIOS = [
  {
    scenario: 'diabetes_follow_up',
    labelKey: 'debugDemo.seedDiabetesSubmit',
    variant: 'theme-button--primary'
  },
  {
    scenario: 'lipid_follow_up',
    labelKey: 'debugDemo.seedLipidSubmit',
    variant: 'theme-button--secondary'
  },
  {
    scenario: 'uric_acid_follow_up',
    labelKey: 'debugDemo.seedUricAcidSubmit',
    variant: 'theme-button--secondary'
  }
];

const idValue = (value) => (value ? String(value) : '');

const findDefaultSelection = (items, defaultID) => {
  const normalizedID = idValue(defaultID);
  if (!normalizedID) {
    return '';
  }
  return items.some((item) => idValue(item.id) === normalizedID) ? normalizedID : '';
};

const findInitialSelection = (items, defaultID) => {
  const defaultSelection = findDefaultSelection(items, defaultID);
  if (defaultSelection) {
    return defaultSelection;
  }
  return items.length > 0 ? idValue(items[0].id) : '';
};

const formatClinicOption = (clinic) => `#${clinic.id} · ${clinic.name || 'Unnamed clinic'}`;

const formatDoctorOption = (doctor) => {
  const username = doctor.username ? ` (${doctor.username})` : '';
  return `#${doctor.id} · ${doctor.name || 'Unnamed doctor'}${username}`;
};

const formatPatientAge = (age, unknownLabel, t) => {
  const normalizedAge = Number(age);
  if (!Number.isFinite(normalizedAge)) {
    return unknownLabel;
  }
  return t('debugDemo.patientAge', { age: normalizedAge });
};

const patientIdentifierPayload = (patient) => {
  const payload = {};
  if (patient?.mrn) {
    payload.mrn = patient.mrn;
  }
  const phone = patient?.phone_e164 || patient?.phone;
  if (phone) {
    payload.phone = phone;
  }
  return payload;
};

const ResultBlock = ({ title, result }) => {
  if (!result) {
    return null;
  }

  return (
    <div className="admin-result-block">
      <h3 className="report-panel-title">{title}</h3>
      <pre>
        {JSON.stringify(result, null, 2)}
      </pre>
    </div>
  );
};

const AdminDebugDemo = () => {
  const { t, i18n } = useTranslation();
  const locale = normalizeUiLocale(i18n.resolvedLanguage || i18n.language);
  const {
    debugEnabled,
    defaultClinicID,
    defaultDoctorID,
    defaultMRN,
    defaultPhone,
    loading: capabilityLoading
  } = useCapability();

  const [clinics, setClinics] = useState([]);
  const [doctors, setDoctors] = useState([]);
  const [directoryLoading, setDirectoryLoading] = useState(false);
  const [directoryError, setDirectoryError] = useState('');
  const [patientFilters, setPatientFilters] = useState(emptyPatientFilters);
  const [appliedPatientFilters, setAppliedPatientFilters] = useState(emptyPatientFilters);
  const [patients, setPatients] = useState([]);
  const [patientsTotal, setPatientsTotal] = useState(0);
  const [patientsLoading, setPatientsLoading] = useState(false);
  const [patientsError, setPatientsError] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [refreshTick, setRefreshTick] = useState(0);
  const [rowActionKey, setRowActionKey] = useState('');
  const [openSeedMenuPatientId, setOpenSeedMenuPatientId] = useState(null);
  const [deletePatientWithReset, setDeletePatientWithReset] = useState(false);
  const [manualOpen, setManualOpen] = useState(false);
  const [seedForm, setSeedForm] = useState(emptySeedForm);
  const [resetForm, setResetForm] = useState(emptyResetForm);
  const [seedResult, setSeedResult] = useState(null);
  const [resetResult, setResetResult] = useState(null);
  const [seedError, setSeedError] = useState('');
  const [resetError, setResetError] = useState('');
  const [seedSubmitting, setSeedSubmitting] = useState(false);
  const [resetSubmitting, setResetSubmitting] = useState(false);

  useEffect(() => {
    setSeedForm((prev) => ({
      ...prev,
      mrn: prev.mrn || defaultMRN || '',
      phone: prev.phone || defaultPhone || ''
    }));
    setResetForm((prev) => ({
      ...prev,
      mrn: prev.mrn || defaultMRN || '',
      phone: prev.phone || defaultPhone || ''
    }));
  }, [defaultMRN, defaultPhone]);

  useEffect(() => {
    if (!debugEnabled) {
      return undefined;
    }

    let cancelled = false;

    const fetchAdminOptions = async () => {
      const token = getAdminToken();
      if (!token) {
        setDirectoryError(t('debugDemo.directoryLoadError'));
        return;
      }

      try {
        setDirectoryLoading(true);
        setDirectoryError('');

        const headers = { Authorization: `Bearer ${token}` };
        const [clinicResponse, doctorResponse] = await Promise.all([
          fetch('/api/v1/clinics?limit=1000', { headers }),
          fetch('/api/v1/doctors?limit=1000', { headers })
        ]);

        if (!clinicResponse.ok || !doctorResponse.ok) {
          throw new Error('Failed to fetch admin options');
        }

        const [clinicData, doctorData] = await Promise.all([
          clinicResponse.json(),
          doctorResponse.json()
        ]);

        const clinicList = Array.isArray(clinicData) ? clinicData : [];
        const doctorList = Array.isArray(doctorData) ? doctorData : [];
        const initialClinicSelection = findInitialSelection(clinicList, defaultClinicID);
        const initialDoctorSelection = findInitialSelection(doctorList, defaultDoctorID);

        if (cancelled) {
          return;
        }

        setClinics(clinicList);
        setDoctors(doctorList);
        setSeedForm((prev) => ({
          ...prev,
          clinic_id: prev.clinic_id || initialClinicSelection,
          doctor_id: prev.doctor_id || initialDoctorSelection
        }));
      } catch (requestError) {
        console.error('Failed to load demo admin options:', requestError);
        if (!cancelled) {
          setDirectoryError(t('debugDemo.directoryLoadError'));
        }
      } finally {
        if (!cancelled) {
          setDirectoryLoading(false);
        }
      }
    };

    fetchAdminOptions();

    return () => {
      cancelled = true;
    };
  }, [debugEnabled, defaultClinicID, defaultDoctorID, t]);

  useEffect(() => {
    if (!debugEnabled) {
      return undefined;
    }

    let cancelled = false;

    const fetchPatients = async () => {
      try {
        setPatientsLoading(true);
        setPatientsError('');
        const offset = (currentPage - 1) * pageSize;
        const params = {
          ...appliedPatientFilters,
          limit: pageSize,
          offset
        };
        const data = await listDebugPatients(params);

        if (cancelled) {
          return;
        }

        setPatients(Array.isArray(data?.patients) ? data.patients : []);
        setPatientsTotal(Number.isFinite(data?.total) ? data.total : 0);
      } catch (err) {
        console.error('Failed to load debug patients:', err);
        if (!cancelled) {
          const message = err?.response?.data?.error || err?.message || t('common.unknownError');
          setPatientsError(`${t('debugDemo.patientListError')}: ${message}`);
        }
      } finally {
        if (!cancelled) {
          setPatientsLoading(false);
        }
      }
    };

    fetchPatients();

    return () => {
      cancelled = true;
    };
  }, [debugEnabled, appliedPatientFilters, currentPage, pageSize, refreshTick, t]);

  const handleSeedChange = (field, value) => {
    setSeedForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleResetChange = (field, value) => {
    setResetForm((prev) => ({ ...prev, [field]: value }));
  };

  const handlePatientFilterChange = (field, value) => {
    setPatientFilters((prev) => ({ ...prev, [field]: value }));
  };

  const applyPatientFilters = (event) => {
    event.preventDefault();
    const trimmedFilters = Object.entries(patientFilters).reduce((acc, [key, value]) => ({
      ...acc,
      [key]: value.trim()
    }), {});
    setCurrentPage(1);
    setAppliedPatientFilters(trimmedFilters);
  };

  const resetPatientFilters = () => {
    setPatientFilters(emptyPatientFilters);
    setAppliedPatientFilters(emptyPatientFilters);
    setCurrentPage(1);
  };

  const refreshPatients = () => {
    setRefreshTick((prev) => prev + 1);
  };

  const buildSeedPayload = () => {
    const payload = {};
    const fields = ['mrn', 'phone', 'patient_name'];
    fields.forEach((key) => {
      const value = seedForm[key]?.trim();
      if (value) {
        payload[key] = value;
      }
    });
    if (seedForm.clinic_id?.trim()) {
      const parsed = parseInt(seedForm.clinic_id, 10);
      if (!Number.isNaN(parsed)) {
        payload.clinic_id = parsed;
      }
    }
    if (seedForm.doctor_id?.trim()) {
      const parsed = parseInt(seedForm.doctor_id, 10);
      if (!Number.isNaN(parsed)) {
        payload.doctor_id = parsed;
      }
    }
    return payload;
  };

  const buildSelectedContextPayload = () => {
    const payload = {};
    if (seedForm.clinic_id?.trim()) {
      const parsed = parseInt(seedForm.clinic_id, 10);
      if (!Number.isNaN(parsed)) {
        payload.clinic_id = parsed;
      }
    }
    if (seedForm.doctor_id?.trim()) {
      const parsed = parseInt(seedForm.doctor_id, 10);
      if (!Number.isNaN(parsed)) {
        payload.doctor_id = parsed;
      }
    }
    return payload;
  };

  const handleSeedScenario = async (scenario) => {
    setSeedError('');
    setSeedResult(null);

    if (!seedForm.mrn.trim() && !seedForm.phone.trim()) {
      setSeedError(t('debugDemo.identifierRequired'));
      return;
    }
    if (!seedForm.clinic_id) {
      setSeedError(t('debugDemo.clinicRequired'));
      return;
    }
    if (!seedForm.doctor_id) {
      setSeedError(t('debugDemo.doctorRequired'));
      return;
    }

    try {
      setSeedSubmitting(true);
      const data = await seedFollowUp({
        ...buildSeedPayload(),
        scenario
      });
      setSeedResult(data);
      refreshPatients();
    } catch (err) {
      console.error('Seed error:', err);
      const message = err?.response?.data?.error || err?.message || t('common.unknownError');
      setSeedError(`${t('debugDemo.seedError')}: ${message}`);
    } finally {
      setSeedSubmitting(false);
    }
  };

  const handleSeedSubmit = async (event) => {
    event.preventDefault();
    await handleSeedScenario('diabetes_follow_up');
  };

  const handleResetSubmit = async (event) => {
    event.preventDefault();
    setResetError('');
    setResetResult(null);

    if (!resetForm.mrn.trim() && !resetForm.phone.trim()) {
      setResetError(t('debugDemo.identifierRequired'));
      return;
    }

    if (!window.confirm(t('debugDemo.resetConfirm'))) {
      return;
    }

    try {
      setResetSubmitting(true);
      const payload = { delete_patient: resetForm.delete_patient };
      if (resetForm.mrn.trim()) payload.mrn = resetForm.mrn.trim();
      if (resetForm.phone.trim()) payload.phone = resetForm.phone.trim();
      const data = await resetPatient(payload);
      setResetResult(data);
      refreshPatients();
    } catch (err) {
      console.error('Reset error:', err);
      const message = err?.response?.data?.error || err?.message || t('common.unknownError');
      setResetError(`${t('debugDemo.resetError')}: ${message}`);
    } finally {
      setResetSubmitting(false);
    }
  };

  const handleRowSeed = async (patient, scenario) => {
    setSeedError('');
    setSeedResult(null);

    if (!seedForm.clinic_id) {
      setSeedError(t('debugDemo.clinicRequired'));
      return;
    }
    if (!seedForm.doctor_id) {
      setSeedError(t('debugDemo.doctorRequired'));
      return;
    }

    const identifiers = patientIdentifierPayload(patient);
    if (!identifiers.mrn && !identifiers.phone) {
      setSeedError(t('debugDemo.rowIdentifierMissing'));
      return;
    }

    const scenarioLabel = t(DEMO_SEED_SCENARIOS.find((item) => item.scenario === scenario)?.labelKey || 'debugDemo.seedSubmit');
    if (!window.confirm(t('debugDemo.seedConfirmForPatient', {
      name: formatPatientName(patient, locale, `#${patient.id}`),
      scenario: scenarioLabel
    }))) {
      return;
    }

    try {
      setRowActionKey(`seed-${scenario}-${patient.id}`);
      const data = await seedFollowUp({
        ...identifiers,
        ...buildSelectedContextPayload(),
        scenario
      });
      setSeedResult(data);
      refreshPatients();
    } catch (err) {
      console.error('Seed row error:', err);
      const message = err?.response?.data?.error || err?.message || t('common.unknownError');
      setSeedError(`${t('debugDemo.seedError')}: ${message}`);
    } finally {
      setRowActionKey('');
    }
  };

  const handleRowReset = async (patient) => {
    setResetError('');
    setResetResult(null);

    const identifiers = patientIdentifierPayload(patient);
    if (!identifiers.mrn && !identifiers.phone) {
      setResetError(t('debugDemo.rowIdentifierMissing'));
      return;
    }

    if (!window.confirm(t('debugDemo.resetConfirmForPatient', {
      name: formatPatientName(patient, locale, `#${patient.id}`)
    }))) {
      return;
    }

    try {
      setRowActionKey(`reset-${patient.id}`);
      const data = await resetPatient({
        ...identifiers,
        delete_patient: deletePatientWithReset
      });
      setResetResult(data);
      refreshPatients();
    } catch (err) {
      console.error('Reset row error:', err);
      const message = err?.response?.data?.error || err?.message || t('common.unknownError');
      setResetError(`${t('debugDemo.resetError')}: ${message}`);
    } finally {
      setRowActionKey('');
    }
  };

  if (capabilityLoading) {
    return (
      <div className="stack">
        <div className="loading-state">
          <div className="loading-orb" />
          <span>{t('common.loading')}</span>
        </div>
      </div>
    );
  }

  if (!debugEnabled) {
    return (
      <div className="stack">
        <div className="page-header">
          <div className="page-title-block">
            <h1 className="page-title">{t('debugDemo.title')}</h1>
          </div>
        </div>
        <div className="status-banner status-banner--error">
          <span>{t('debugDemo.disabled')}</span>
        </div>
      </div>
    );
  }

  const defaultClinicSelection = findInitialSelection(clinics, defaultClinicID);
  const defaultDoctorSelection = findInitialSelection(doctors, defaultDoctorID);
  const defaultClinicMissing = Boolean(defaultClinicID) && clinics.length > 0 && !defaultClinicSelection;
  const defaultDoctorMissing = Boolean(defaultDoctorID) && doctors.length > 0 && !defaultDoctorSelection;
  const seedSelectionReady = Boolean(seedForm.clinic_id && seedForm.doctor_id);
  const totalPages = Math.max(1, Math.ceil(patientsTotal / pageSize));
  const activeRowAction = Boolean(rowActionKey);

  return (
    <div className="admin-page">
      <div className="page-header admin-header-panel">
        <div className="page-title-block">
          <h1 className="page-title">{t('debugDemo.title')}</h1>
          <p className="page-subtitle">{t('debugDemo.pageDescription')}</p>
        </div>
        <div className="page-actions">
          <button
            type="button"
            className="theme-button theme-button--secondary"
            onClick={refreshPatients}
            disabled={patientsLoading}
          >
            {t('common.refresh')}
          </button>
        </div>
      </div>

      {directoryError && (
        <div className="status-banner status-banner--error">
          <span>{directoryError}</span>
        </div>
      )}

      {(seedError || resetError || patientsError) && (
        <div className="status-banner status-banner--error">
          <span>{seedError || resetError || patientsError}</span>
        </div>
      )}

      <section className="surface-panel admin-panel">
        <div className="surface-panel__body stack">
          <div className="page-title-block">
            <h2 className="report-panel-title">{t('debugDemo.patientListTitle')}</h2>
            <p className="page-subtitle">{t('debugDemo.patientListDescription')}</p>
          </div>

          <form onSubmit={applyPatientFilters} className="debug-patient-filter-row">
            <div className="field">
              <label className="field__label" htmlFor="patient-filter-mrn">{t('debugDemo.mrn')}</label>
              <input
                id="patient-filter-mrn"
                className="theme-input"
                type="text"
                value={patientFilters.mrn}
                onChange={(event) => handlePatientFilterChange('mrn', event.target.value)}
                placeholder={t('debugDemo.mrnPlaceholder')}
              />
            </div>

            <div className="field">
              <label className="field__label" htmlFor="patient-filter-phone">{t('debugDemo.phone')}</label>
              <input
                id="patient-filter-phone"
                className="theme-input"
                type="text"
                value={patientFilters.phone}
                onChange={(event) => handlePatientFilterChange('phone', event.target.value)}
                placeholder={t('debugDemo.phonePlaceholder')}
              />
            </div>

            <div className="field">
              <label className="field__label" htmlFor="patient-filter-first-name">{t('debugDemo.firstName')}</label>
              <input
                id="patient-filter-first-name"
                className="theme-input"
                type="text"
                value={patientFilters.first_name}
                onChange={(event) => handlePatientFilterChange('first_name', event.target.value)}
                placeholder={t('debugDemo.firstNamePlaceholder')}
              />
            </div>

            <div className="field">
              <label className="field__label" htmlFor="patient-filter-last-name">{t('debugDemo.lastName')}</label>
              <input
                id="patient-filter-last-name"
                className="theme-input"
                type="text"
                value={patientFilters.last_name}
                onChange={(event) => handlePatientFilterChange('last_name', event.target.value)}
                placeholder={t('debugDemo.lastNamePlaceholder')}
              />
            </div>

            <button type="submit" className="theme-button theme-button--primary" disabled={patientsLoading}>
              {t('common.search')}
            </button>
            <button type="button" className="theme-button theme-button--ghost" onClick={resetPatientFilters}>
              {t('common.reset')}
            </button>
          </form>

          <div className="form-grid">
            <div className="field">
              <label className="field__label" htmlFor="row-clinic-id">{t('debugDemo.clinicId')}</label>
              <select
                id="row-clinic-id"
                className="theme-select"
                value={seedForm.clinic_id}
                onChange={(event) => handleSeedChange('clinic_id', event.target.value)}
                disabled={directoryLoading || clinics.length === 0}
              >
                <option value="">
                  {directoryLoading ? t('common.loading') : t('debugDemo.selectClinic')}
                </option>
                {clinics.map((clinic) => (
                  <option key={clinic.id} value={idValue(clinic.id)}>
                    {formatClinicOption(clinic)}
                  </option>
                ))}
              </select>
              <span className="field__hint">
                {!seedForm.clinic_id && defaultClinicMissing
                  ? t('debugDemo.defaultClinicMissing', { id: defaultClinicID })
                  : t(seedForm.clinic_id ? 'debugDemo.selectionLocked' : 'debugDemo.clinicRequired')}
              </span>
            </div>

            <div className="field">
              <label className="field__label" htmlFor="row-doctor-id">{t('debugDemo.doctorId')}</label>
              <select
                id="row-doctor-id"
                className="theme-select"
                value={seedForm.doctor_id}
                onChange={(event) => handleSeedChange('doctor_id', event.target.value)}
                disabled={directoryLoading || doctors.length === 0}
              >
                <option value="">
                  {directoryLoading ? t('common.loading') : t('debugDemo.selectDoctor')}
                </option>
                {doctors.map((doctor) => (
                  <option key={doctor.id} value={idValue(doctor.id)}>
                    {formatDoctorOption(doctor)}
                  </option>
                ))}
              </select>
              <span className="field__hint">
                {!seedForm.doctor_id && defaultDoctorMissing
                  ? t('debugDemo.defaultDoctorMissing', { id: defaultDoctorID })
                  : t(seedForm.doctor_id ? 'debugDemo.selectionLocked' : 'debugDemo.doctorRequired')}
              </span>
            </div>

            <label className="admin-checkbox-field">
              <input
                type="checkbox"
                checked={deletePatientWithReset}
                onChange={(event) => setDeletePatientWithReset(event.target.checked)}
              />
              <span>{t('debugDemo.deletePatient')}</span>
            </label>
          </div>

          {patientsLoading ? (
            <div className="loading-state">
              <div className="loading-orb" />
              <span>{t('common.loading')}</span>
            </div>
          ) : patients.length === 0 ? (
            <div className="empty-state">
              <h3>{t('debugDemo.noPatients')}</h3>
              <p>{t('debugDemo.noPatientsHint')}</p>
            </div>
          ) : (
            <div className="data-table-shell">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>{t('debugDemo.patientColumnId')}</th>
                    <th>{t('debugDemo.patientColumnName')}</th>
                    <th>{t('debugDemo.mrn')}</th>
                    <th>{t('debugDemo.phone')}</th>
                    <th>{t('debugDemo.patientColumnProfile')}</th>
                    <th>{t('debugDemo.patientColumnCreatedAt')}</th>
                    <th>{t('reports.actions')}</th>
                  </tr>
                </thead>
                <tbody>
                  {patients.map((patient) => {
                    const resetKey = `reset-${patient.id}`;
                    const isResetLoading = rowActionKey === resetKey;
                    const isAnySeedLoading = rowActionKey.startsWith('seed-') && rowActionKey.endsWith(`-${patient.id}`);

                    return (
                      <tr key={patient.id}>
                        <td>
                          <div className="table-title">#{patient.id}</div>
                          {patient.patient_id && (
                            <div className="table-meta">{patient.patient_id}</div>
                          )}
                        </td>
                        <td>
                          <div className="table-title">
                            <bdi dir="auto">{formatPatientName(patient, locale, t('common.unknown'))}</bdi>
                          </div>
                        </td>
                        <td>{patient.mrn || '-'}</td>
                        <td>{patient.phone_e164 || patient.phone || '-'}</td>
                        <td>
                          <div className="table-title">{patient.gender || '-'}</div>
                          <div className="table-meta">
                            {formatPatientAge(patient.age, t('common.unknown'), t)}
                          </div>
                        </td>
                        <td>{formatDateTime(patient.created_at, locale) || '-'}</td>
                        <td>
                          <div className="table-actions">
                            <div className="split-action">
                              <button
                                type="button"
                                className="theme-button theme-button--secondary theme-button--compact split-action__trigger"
                                onClick={() => setOpenSeedMenuPatientId((current) => current === patient.id ? null : patient.id)}
                                disabled={activeRowAction || directoryLoading || !seedSelectionReady}
                                aria-haspopup="menu"
                                aria-expanded={openSeedMenuPatientId === patient.id}
                                aria-controls={`debug-patient-seed-menu-${patient.id}`}
                              >
                                <span>{isAnySeedLoading ? t('common.submitting') : t('debugDemo.seedMenuTrigger')}</span>
                                <span className="split-action__chevron" aria-hidden="true">▾</span>
                              </button>
                              {openSeedMenuPatientId === patient.id && (
                                <div
                                  id={`debug-patient-seed-menu-${patient.id}`}
                                  className="split-action__dropdown"
                                  role="menu"
                                  aria-label={t('debugDemo.seedMenuLabel')}
                                >
                                  {DEMO_SEED_SCENARIOS.map((seedScenario) => (
                                    <button
                                      key={seedScenario.scenario}
                                      type="button"
                                      className="split-action__item"
                                      role="menuitem"
                                      onClick={() => {
                                        setOpenSeedMenuPatientId(null);
                                        handleRowSeed(patient, seedScenario.scenario);
                                      }}
                                    >
                                      {t(seedScenario.labelKey)}
                                    </button>
                                  ))}
                                </div>
                              )}
                            </div>
                            <button
                              type="button"
                              className="theme-button theme-button--danger theme-button--compact"
                              onClick={() => handleRowReset(patient)}
                              disabled={activeRowAction}
                            >
                              {isResetLoading ? t('common.submitting') : t('debugDemo.resetSubmit')}
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>

              <Pagination
                currentPage={currentPage}
                totalPages={totalPages}
                totalCount={patientsTotal}
                pageSize={pageSize}
                onPageChange={setCurrentPage}
                onPageSizeChange={(nextPageSize) => {
                  setPageSize(nextPageSize);
                  setCurrentPage(1);
                }}
              />
            </div>
          )}

          <ResultBlock title={t('debugDemo.result')} result={seedResult || resetResult} />
        </div>
      </section>

      <section className="surface-panel admin-panel">
        <div className="surface-panel__body stack">
          <div className="page-header admin-section-header">
            <div className="page-title-block">
              <h2 className="report-panel-title">{t('debugDemo.manualTitle')}</h2>
              <p className="page-subtitle">{t('debugDemo.manualDescription')}</p>
            </div>
            <button
              type="button"
              className="theme-button theme-button--secondary"
              onClick={() => setManualOpen((prev) => !prev)}
            >
              {manualOpen ? t('debugDemo.hideManualMode') : t('debugDemo.showManualMode')}
            </button>
          </div>

          {manualOpen && (
            <div className="stack">
              <div className="page-title-block">
                <h3 className="report-panel-title">{t('debugDemo.seedTitle')}</h3>
              </div>

              <form onSubmit={handleSeedSubmit} className="stack">
                <div className="form-grid">
                  <div className="field">
                    <label className="field__label" htmlFor="seed-mrn">{t('debugDemo.mrn')}</label>
                    <input
                      id="seed-mrn"
                      className="theme-input"
                      type="text"
                      value={seedForm.mrn}
                      onChange={(event) => handleSeedChange('mrn', event.target.value)}
                      placeholder={t('debugDemo.mrnPlaceholder')}
                    />
                  </div>

                  <div className="field">
                    <label className="field__label" htmlFor="seed-phone">{t('debugDemo.phone')}</label>
                    <input
                      id="seed-phone"
                      className="theme-input"
                      type="text"
                      value={seedForm.phone}
                      onChange={(event) => handleSeedChange('phone', event.target.value)}
                      placeholder={t('debugDemo.phonePlaceholder')}
                    />
                  </div>

                  <div className="field">
                    <label className="field__label" htmlFor="seed-patient-name">{t('debugDemo.patientName')}</label>
                    <input
                      id="seed-patient-name"
                      className="theme-input"
                      type="text"
                      value={seedForm.patient_name}
                      onChange={(event) => handleSeedChange('patient_name', event.target.value)}
                      placeholder={t('debugDemo.patientNamePlaceholder')}
                    />
                  </div>
                </div>

                <div className="admin-actions-inline">
                  {DEMO_SEED_SCENARIOS.map((seedScenario) => (
                    <button
                      key={seedScenario.scenario}
                      type="button"
                      className={`theme-button ${seedScenario.variant}`}
                      onClick={() => handleSeedScenario(seedScenario.scenario)}
                      disabled={seedSubmitting || directoryLoading || !seedSelectionReady}
                    >
                      {seedSubmitting ? t('common.submitting') : t(seedScenario.labelKey)}
                    </button>
                  ))}
                  <button
                    type="button"
                    className="theme-button theme-button--ghost"
                    onClick={() => {
                      setSeedForm({
                        ...emptySeedForm,
                        clinic_id: defaultClinicSelection,
                        doctor_id: defaultDoctorSelection,
                        mrn: defaultMRN || '',
                        phone: defaultPhone || ''
                      });
                      setSeedResult(null);
                      setSeedError('');
                    }}
                  >
                    {t('common.reset')}
                  </button>
                </div>
              </form>

              <div className="page-title-block">
                <h3 className="report-panel-title">{t('debugDemo.resetTitle')}</h3>
              </div>

              <form onSubmit={handleResetSubmit} className="stack">
                <div className="form-grid">
                  <div className="field">
                    <label className="field__label" htmlFor="reset-mrn">{t('debugDemo.mrn')}</label>
                    <input
                      id="reset-mrn"
                      className="theme-input"
                      type="text"
                      value={resetForm.mrn}
                      onChange={(event) => handleResetChange('mrn', event.target.value)}
                      placeholder={t('debugDemo.mrnPlaceholder')}
                    />
                  </div>

                  <div className="field">
                    <label className="field__label" htmlFor="reset-phone">{t('debugDemo.phone')}</label>
                    <input
                      id="reset-phone"
                      className="theme-input"
                      type="text"
                      value={resetForm.phone}
                      onChange={(event) => handleResetChange('phone', event.target.value)}
                      placeholder={t('debugDemo.phonePlaceholder')}
                    />
                  </div>
                </div>

                <label className="admin-checkbox-field">
                  <input
                    type="checkbox"
                    checked={resetForm.delete_patient}
                    onChange={(event) => handleResetChange('delete_patient', event.target.checked)}
                  />
                  <span>{t('debugDemo.deletePatient')}</span>
                </label>

                <div className="admin-actions-inline">
                  <button
                    type="submit"
                    className="theme-button theme-button--danger"
                    disabled={resetSubmitting}
                  >
                    {resetSubmitting ? t('common.submitting') : t('debugDemo.resetSubmit')}
                  </button>
                  <button
                    type="button"
                    className="theme-button theme-button--ghost"
                    onClick={() => {
                      setResetForm({
                        ...emptyResetForm,
                        mrn: defaultMRN || '',
                        phone: defaultPhone || ''
                      });
                      setResetResult(null);
                      setResetError('');
                    }}
                  >
                    {t('common.reset')}
                  </button>
                </div>
              </form>

              <ResultBlock title={t('debugDemo.result')} result={seedResult || resetResult} />
            </div>
          )}
        </div>
      </section>
    </div>
  );
};

export default AdminDebugDemo;
